import { ApolloClient } from "@apollo/client";
import { fail } from "@homebound/activesupport";
import { AmplifyConfig, createApolloClientWithAuth } from "@homebound/auth-components";
import merge from "lodash/merge";
import { typePolicies } from "src/typePolicies";
import { possibleTypes } from "~generated/graphqlTypes";

/** Flags the user's browser to use the internal auth pool on next load. */
export const useInternalPoolKey = "useInternalPoolKey";
export type Stage = "local" | "dev" | "qa" | "local-prod" | "prod";

export function getStage(windowLocation = window.location): Stage {
  // Guess stage based on the current window.location.hostname
  const hostname = windowLocation.hostname;
  if (hostname.includes("dev-homebound")) {
    return "dev";
  } else if (hostname.includes("qa-homebound")) {
    return "qa";
  } else if (hostname.includes("localhost.design.homebound.com")) {
    return "local-prod";
  } else if (hostname.includes("homebound")) {
    return "prod";
  } else {
    return "local";
  }
}

export function getGraphQLBaseUrl(stage: Stage = getStage()): string {
  const stageToGraphQL: Record<Stage, string> = {
    local: VITE_GRAPHQL_SERVICE ? VITE_GRAPHQL_SERVICE : `http://localhost:${GQL_PORT}`,
    dev: "https://graphql.dev-homebound.com",
    qa: `https://${qaServerEnvironment().subDomain}.qa-homebound.com`,
    "local-prod": "https://graphql.homebound.com",
    prod: "https://graphql.homebound.com",
  };
  return stageToGraphQL[stage];
}

export function createApolloClient(stage: Stage): ApolloClient<unknown> {
  const baseUrl = getGraphQLBaseUrl(stage);
  return createApolloClientWithAuth(`${baseUrl}/graphql`, {
    serviceName: "plan-configuration-frontend",
    serviceVersion: VITE_GIT_COMMIT ?? "local",
    retryLinkOptions: {
      attempts: {
        retryIf(error: any) {
          if (error instanceof TypeError) {
            console.error("Retrying because fetched rejected: " + error.message);
          }
          // For a failed mutation, error will be a ServerError (error.name === 'ServerName'), don't retry
          // For a rejected fetch (likely transient connectivity issue), error will be a TypeError, so retry
          return error instanceof TypeError;
        },
      },
    },
    apolloClientOptions: {
      connectToDevTools: stage !== "prod" && stage !== "local-prod",
      assumeImmutableResults: true,
      defaultOptions: {
        // Prefer consistency on CRUD tables/forms. Use watchQuery b/c that's what React's hooks use.
        // For nextFetchPolicy, see https://github.com/apollographql/apollo-client/pull/6712
        watchQuery: { fetchPolicy: "network-only", nextFetchPolicy: "cache-first" },
        query: { fetchPolicy: "network-only" },
      },
    },
    inMemoryCacheOptions: {
      possibleTypes,
      typePolicies,
    },
  });
}

// Include `aws.cognito.signin.user.admin` so that we can access user attributes like their profile picture:
// https://stackoverflow.com/a/53151260/355031
const internalScope = ["phone", "profile", "email", "openid", "aws.cognito.signin.user.admin"];
// We aren't allowed to ask homeowner users for phone since their outside our org (or maybe we need more permissions on auth)
const homeownerScope = ["profile", "email", "openid", "aws.cognito.signin.user.admin"];
/**
 * Auth configuration differences between internal and homeowner user pools
 */
const authPoolConfig = {
  prod: {
    internal: {
      userPoolId: "us-west-2_Sud41U2uX",
      userPoolWebClientId: "6jbhcfnuji5mor48dg2hg37hvg",
      oauthDomain: "homebound-internal-prod.auth.us-west-2.amazoncognito.com",
    },
    homeowner: {
      userPoolId: "us-west-2_EwgDlC8SO",
      userPoolWebClientId: "2jm02vg56djti5s38h35cvuukh",
      oauthDomain: "homeowner-auth.homebound.com",
      googleClientId: "143905945782-fotv6p2muflc4i2vo0iakjfringdpehl.apps.googleusercontent.com",
    },
  },
  nonProd: {
    internal: {
      userPoolId: "us-west-2_u4KsZS7DF",
      userPoolWebClientId: "47jne7hh7uao4il33eq1kftr5c",
      oauthDomain: "homebound-internal-dev.auth.us-west-2.amazoncognito.com",
    },
    homeowner: {
      userPoolId: "us-west-2_02OX5J146",
      userPoolWebClientId: "467351l744rcg2nheppufd05fm",
      oauthDomain: "homeowner-auth.dev-homebound.com",
      googleClientId: "503475035999-48hfp9gbddof3knruok3ls2ggvqv4igd.apps.googleusercontent.com",
    },
  },
} as const;

// Checks if we're an existing returning user coming back from a previous session
// so we initialize the correct auth pool
export function initialAmplifyConfig() {
  const stage = getStage();

  const detectedIsInternal = shouldUseInternalAuthPool(stage === "prod" || stage === "local-prod");

  return amplifyConfig(stage, detectedIsInternal);
}

/**
 * Get Google OAuth Client ID for direct Google OAuth (not via Cognito)
 * We only use this for public sign-up flows
 */
export function getGoogleClientId(stage: Stage = getStage()): string {
  const isProd = stage === "prod" || stage === "local-prod";
  const poolConfig = authPoolConfig[isProd ? "prod" : "nonProd"]["homeowner"];
  return poolConfig.googleClientId;
}

export function amplifyConfig(stage: Stage, useInternalAuthPool: boolean): AmplifyConfig {
  const isProd = stage === "prod" || stage === "local-prod";
  const poolConfig = authPoolConfig[isProd ? "prod" : "nonProd"][useInternalAuthPool ? "internal" : "homeowner"];

  function stageConfig() {
    return {
      Auth: {
        userPoolId: poolConfig.userPoolId,
        userPoolWebClientId: poolConfig.userPoolWebClientId,
        // Only use CUSTOM_AUTH for external/homeowner pool (magic links)
        // Internal pool uses Google OAuth (federated identity)
        ...(!useInternalAuthPool ? { authenticationFlowType: "CUSTOM_AUTH" } : {}),
        oauth: { domain: poolConfig.oauthDomain },
        // Use cookie storage so that non-GraphQL calls (i.e. image downloads) still get the auth token only if internal auth pool
        // TODO: Note: this doesn't really seem necessary anymore but leaving it in here now to remain aligned with how BP, our main gql consumer, handles auth
        ...(useInternalAuthPool
          ? {
              cookieStorage: isProd
                ? { domain: "design.homebound.com" }
                : {
                    // use whole domain for preview branches to reuse auth
                    domain: stage === "local" ? "localhost" : `.${stage}-homebound.com`,
                    // Don't use secure so that the cookie works on the http://localhost:4000/graphql GraphQL playground.
                    secure: false,
                  },
            }
          : {}),
      },
    };
  }

  const { origin } = window.location;

  return merge({}, stageConfig(), {
    Auth: {
      region: "us-west-2",
      oauth: {
        scope: useInternalAuthPool ? internalScope : homeownerScope,
        redirectSignIn: origin + "/auth/gcallback",
        redirectSignOut: origin + "/",
        responseType: "code" as const,
      },
    },
  });
}

function extractClientIdsFromCookies(): string[] {
  // CognitoIdentityServiceProvider.{clientId}.{rest}
  const regex = /CognitoIdentityServiceProvider\.([^.]+)\./g;
  const matches = Array.from(document.cookie.matchAll(regex));
  return [...new Set(matches.map((match) => match[1]))];
}

/**
 * Returns true if we should use the internal user pool.
 *
 * We look at both "which pool created our cookies?" as well as the `useInternalAuthPool` hint.
 */
export function shouldUseInternalAuthPool(isProd: boolean): boolean {
  const useInternalPool = localStorage.getItem(useInternalPoolKey) === "true";
  // If we're coming from an internal redirect, we need to clear the cookie and return true
  if (useInternalPool) {
    localStorage.removeItem(useInternalPoolKey);
    return true;
  }
  const clientIds = extractClientIdsFromCookies();
  if (clientIds.length === 0) return false;
  const stageConfig = isProd ? authPoolConfig.prod : authPoolConfig.nonProd;
  // In practice, this will only ever be one clientId but it could technically be multiple so
  // we just loop and are very explicit with our check
  for (const clientId of clientIds) {
    if (stageConfig.internal.userPoolWebClientId === clientId) return true;
    if (stageConfig.homeowner.userPoolWebClientId === clientId) return false;
  }
  return false;
}

/**
 * Detects if our JS build, either locally or in a preview branch, is running in prod qa,
 * or a feature branch qa.
 *
 * Unlike the backend, the frontend will auto-detect "I'm on a qa/feature branch" and use the
 * appropriate backend, based on the branch naming convention, without any actual deployment/infra
 * changes i.e. in the `stacks` repo.
 */
function qaServerEnvironment(windowLocation = window.location) {
  if (VITE_GIT_BRANCH?.startsWith("qa-")) {
    // Support FE branch names the 'main' qa branch, `qa-1234`, as well as `qa-1234@sub-ticket` WIP branches
    const qaCustomBeBranch =
      VITE_GIT_BRANCH.split("@").first ??
      fail("Invalid BE Feature Branch - please ensure branch name matches pattern 'qa-[story|epic]@[sub-story-branch]");
    // Feature branch qa uses `qa-1234.graphql.dev-homebound.com` subdomains within the dev infra
    return { subDomain: `${qaCustomBeBranch}.graphql`, qaCustomBeBranch };
  }
  // Prod qa uses the top-level `graphql.qa-homebound.com` domain, so doesn't need a subdomain
  return { subDomain: "graphql", qaCustomBeBranch: undefined };
}
